const drawerSelector = '[aria-controls="cart-drawer"], [aria-controls="cart-gift-wrap-drawer"]';

const updateCartNoteToggleState = (noteField) => {
  if (!noteField || noteField.getAttribute("name") !== "note") {
    return;
  }

  const noteDialog = noteField.closest("cart-note-dialog");

  if (!noteDialog?.id) {
    return;
  }

  const toggleCheckbox = document.querySelector(`[aria-controls="${noteDialog.id}"] .checkbox`);

  if (!toggleCheckbox) {
    return;
  }

  toggleCheckbox.classList.toggle("is-checked", noteField.value.trim().length > 0);
};

const updateDrawerTopOffset = () => {
  const header = document.querySelector(".shopify-section--header");
  const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
  const offset = Math.max(0, Math.min(headerBottom, window.innerHeight));

  document.documentElement.style.setProperty("--drawer-top-offset", `${offset}px`);
};

document.addEventListener(
  "click",
  (event) => {
    if (!event.target.closest(drawerSelector)) {
      return;
    }

    updateDrawerTopOffset();
  },
  true
);

document.addEventListener(
  "keydown",
  (event) => {
    if (!["Enter", " "].includes(event.key) || !event.target.closest(drawerSelector)) {
      return;
    }

    updateDrawerTopOffset();
  },
  true
);

document.addEventListener("input", (event) => {
  updateCartNoteToggleState(event.target);
});

document.addEventListener("change", (event) => {
  updateCartNoteToggleState(event.target);
});

const BaseDrawer = window.customElements.get("x-drawer");

if (BaseDrawer && !window.customElements.get("cart-gift-wrap-drawer")) {
  class CartGiftWrapDrawer extends BaseDrawer {
    connectedCallback() {
      super.connectedCallback();

      this.giftWrapAbortController = new AbortController();
      this.addEventListener("dialog:before-show", this.reset.bind(this), { signal: this.giftWrapAbortController.signal });
      this.addEventListener("input", this.onQuantityChange.bind(this), { signal: this.giftWrapAbortController.signal });
      this.addEventListener("change", this.onQuantityChange.bind(this), { signal: this.giftWrapAbortController.signal });
      this.addEventListener("click", this.onQuantityButtonClick.bind(this), { signal: this.giftWrapAbortController.signal });
      this.addEventListener("submit", this.onSubmit.bind(this), { signal: this.giftWrapAbortController.signal });
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      this.giftWrapAbortController?.abort();
    }

    get form() {
      return this.querySelector(".cart-gift-wrap-drawer__form");
    }

    get footer() {
      return this.querySelector("[data-gift-wrap-unit-price]");
    }

    get quantityInput() {
      return this.form?.elements["quantity"];
    }

    get submitButtons() {
      return Array.from(this.querySelectorAll(`[form="${this.form?.id}"][type="submit"]`));
    }

    formatMoney(cents) {
      const format = window.themeVariables?.settings?.moneyWithCurrencyFormat || "${{amount}}";
      const value = cents / 100;
      const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
        const parts = number.toFixed(precision).split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

        return parts.join(decimal);
      };

      return format.replace(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/g, (_, placeholder) => {
        if (placeholder === "amount_no_decimals") {
          return formatWithDelimiters(value, 0);
        }

        if (placeholder === "amount_with_comma_separator") {
          return formatWithDelimiters(value, 2, ".", ",");
        }

        if (placeholder === "amount_no_decimals_with_comma_separator") {
          return formatWithDelimiters(value, 0, ".", ",");
        }

        return formatWithDelimiters(value);
      });
    }

    getQuantity() {
      const quantityInput = this.quantityInput;
      const minimumQuantity = parseInt(quantityInput?.min || "1");
      const currentQuantity = parseInt(quantityInput?.value || `${minimumQuantity}`);

      return Math.max(Number.isNaN(currentQuantity) ? minimumQuantity : currentQuantity, minimumQuantity);
    }

    updateTotalPrice() {
      const priceElement = this.footer?.querySelector("[data-gift-wrap-total-price]");

      if (!this.footer || !priceElement) {
        return;
      }

      const unitPrice = parseInt(this.footer.getAttribute("data-gift-wrap-unit-price") || "0");
      priceElement.textContent = this.formatMoney(unitPrice * this.getQuantity());
    }

    clearError() {
      this.querySelector("[data-gift-wrap-error]")?.replaceChildren();
    }

    showError(message) {
      const errorContainer = this.querySelector("[data-gift-wrap-error]");

      if (!errorContainer) {
        return;
      }

      errorContainer.innerHTML = `<div class="banner banner--error" role="alert" aria-live="polite">${message}</div>`;
    }

    getDefaultErrorMessage() {
      return this.getAttribute("data-error-message") || "";
    }

    reset() {
      if (!this.form) {
        return;
      }

      this.form.reset();
      this.form.querySelector("quantity-selector")?.restoreDefaultValue();
      this.clearError();
      this.submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
      this.updateTotalPrice();
    }

    onQuantityChange(event) {
      if (!event.target.closest('.cart-gift-wrap-drawer__form [name="quantity"]')) {
        return;
      }

      this.updateTotalPrice();
    }

    onQuantityButtonClick(event) {
      if (!event.target.closest(".cart-gift-wrap-drawer__form .quantity-selector__button")) {
        return;
      }

      requestAnimationFrame(() => this.updateTotalPrice());
    }

    async onSubmit(event) {
      if (event.target !== this.form) {
        return;
      }

      event.preventDefault();

      const submitter = event.submitter;
      const action = submitter?.getAttribute("data-gift-wrap-action") || "add";

      if (submitter?.getAttribute("aria-busy") === "true") {
        return;
      }

      this.submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));
      this.clearError();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
      sectionsToBundle.push("cart-gift-wrap-drawer");
      sectionsToBundle = [...new Set(sectionsToBundle)];

      const lineKey = this.form.elements["line_key"]?.value;
      let response;

      try {
        if (action === "remove" && lineKey) {
          response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: lineKey,
              quantity: 0,
              sections: sectionsToBundle.join(",")
            })
          });
        } else if (action === "update" && lineKey) {
          response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: lineKey,
              quantity: this.getQuantity(),
              properties: {},
              sections: sectionsToBundle.join(",")
            })
          });
        } else {
          const formData = new FormData();
          formData.set("id", this.form.elements["id"].value);
          formData.set("quantity", this.getQuantity());

          formData.set("sections", sectionsToBundle.join(","));
          formData.set("sections_url", `${Shopify.routes.root}cart`);

          response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            body: formData,
            method: "POST",
            headers: { "X-Requested-With": "XMLHttpRequest" }
          });
        }
      } catch (error) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
        this.showError(this.getDefaultErrorMessage());
        this.submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
        return;
      }

      const responseJson = await response.json().catch(() => ({}));
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!response.ok) {
        const errorMessage = responseJson["description"] || responseJson["message"] || this.getDefaultErrorMessage();
        this.showError(errorMessage);
        this.submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
        return;
      }

      const cartContent = action === "add" ? await (await fetch(`${Shopify.routes.root}cart.js`)).json() : responseJson;

      if (action === "add") {
        cartContent["sections"] = responseJson["sections"];
      }

      await this.hide();

      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
        return;
      }

      const drawerHtml = cartContent["sections"]?.["cart-gift-wrap-drawer"];

      if (drawerHtml) {
        const newDrawer = new DOMParser().parseFromString(drawerHtml, "text/html").querySelector("#cart-gift-wrap-drawer");

        if (newDrawer) {
          this.replaceWith(newDrawer);
        }
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "gift-wrap:change",
            cart: cartContent
          }
        })
      );
    }
  }

  window.customElements.define("cart-gift-wrap-drawer", CartGiftWrapDrawer);
}
